/**
 * Tightens SVG viewBox/width/height to content bounds.
 * Design exports often ship with empty canvas padding on one side; that
 * padding makes object-contain logos look off-center.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
/** Skip rewrite when unused canvas on every side is below this fraction. */
const TIGHT_RATIO = 0.02;
const PAD_PX = 1;

function isSvgFile(file: File): boolean {
  if (file.type === "image/svg+xml") return true;
  return file.name.toLowerCase().endsWith(".svg");
}

function parseViewBox(value: string | null): {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
} | null {
  if (value === null) return null;
  const parts: number[] = value
    .trim()
    .split(/[\s,]+/)
    .map((part: string) => Number(part));
  if (parts.length !== 4) return null;
  const x: number | undefined = parts[0];
  const y: number | undefined = parts[1];
  const w: number | undefined = parts[2];
  const h: number | undefined = parts[3];
  if (
    x === undefined ||
    y === undefined ||
    w === undefined ||
    h === undefined ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    w <= 0 ||
    h <= 0
  ) {
    return null;
  }
  return { x, y, w, h };
}

function alreadyTight(
  viewBox: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  },
  content: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  },
): boolean {
  const left: number = content.x - viewBox.x;
  const top: number = content.y - viewBox.y;
  const right: number = viewBox.x + viewBox.w - (content.x + content.w);
  const bottom: number = viewBox.y + viewBox.h - (content.y + content.h);
  return (
    left <= viewBox.w * TIGHT_RATIO &&
    right <= viewBox.w * TIGHT_RATIO &&
    top <= viewBox.h * TIGHT_RATIO &&
    bottom <= viewBox.h * TIGHT_RATIO
  );
}

/**
 * Returns tightened SVG markup, or null when trim is unnecessary / impossible.
 */
export function trimSvgMarkupViewBox(svgText: string): string | null {
  if (typeof document === "undefined") return null;

  const doc: Document = new DOMParser().parseFromString(
    svgText,
    "image/svg+xml",
  );
  if (doc.querySelector("parsererror") !== null) return null;

  const root: Element = doc.documentElement;
  if (root.namespaceURI !== SVG_NS || root.localName !== "svg") return null;

  const host: HTMLDivElement = document.createElement("div");
  host.style.cssText =
    "position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden;visibility:hidden;pointer-events:none";
  const svg: SVGSVGElement = document.importNode(root, true) as SVGSVGElement;
  host.appendChild(svg);
  document.body.appendChild(host);

  let bbox: DOMRect;
  try {
    bbox = svg.getBBox();
  } catch {
    document.body.removeChild(host);
    return null;
  }
  document.body.removeChild(host);

  if (!(bbox.width > 0) || !(bbox.height > 0)) return null;

  const content = {
    x: bbox.x,
    y: bbox.y,
    w: bbox.width,
    h: bbox.height,
  };
  const currentVb =
    parseViewBox(svg.getAttribute("viewBox")) ??
    ({
      x: 0,
      y: 0,
      w: Number(svg.getAttribute("width")) || content.w,
      h: Number(svg.getAttribute("height")) || content.h,
    } as const);

  if (alreadyTight(currentVb, content)) return null;

  const x: number = content.x - PAD_PX;
  const y: number = content.y - PAD_PX;
  const w: number = content.w + PAD_PX * 2;
  const h: number = content.h + PAD_PX * 2;
  svg.setAttribute("viewBox", `${x} ${y} ${w} ${h}`);
  svg.setAttribute("width", String(w));
  svg.setAttribute("height", String(h));

  return new XMLSerializer().serializeToString(svg);
}

/** Trims empty SVG canvas padding before brand-logo upload. */
export async function trimSvgViewBoxIfNeeded(file: File): Promise<File> {
  if (!isSvgFile(file)) return file;

  let text: string;
  try {
    text = await file.text();
  } catch {
    return file;
  }

  const trimmed: string | null = trimSvgMarkupViewBox(text);
  if (trimmed === null) return file;

  return new File([trimmed], file.name, {
    type: "image/svg+xml",
    lastModified: file.lastModified,
  });
}
